import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Boxes, Cpu, Crosshair, Check, Rocket } from 'lucide-react';
import { skus, models } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { MetricCard } from '../components/MetricCard';
import type { SkuSummary, ModelType, SkuStatus } from '../skus/types';

const MODEL_TYPE_LABEL: Record<ModelType, string> = {
  qc: 'Quality Control',
  '6dof': '6DoF Pose',
};

const STATUS_COLOR: Record<SkuStatus, string> = {
  draft: 'text-gray-400',
  capturing: 'text-amber-400',
  dataset_ready: 'text-blue-400',
  published: 'text-emerald-400',
};

export default function SkusPage() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['skus', 'list'],
    queryFn: skus.list,
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const canTrain = user?.org_type === 'kinetica' || user?.org_type === 'integrator';
  const trainMutation = useMutation({
    mutationFn: (vars: { skuId: string; version: string }) =>
      models.create({ sku_id: vars.skuId, dataset_version: vars.version }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['models', 'jobs'] });
      navigate('/models');
    },
  });

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-red-400 mb-2">Failed to load SKUs</p>
        <p className="text-xs text-gray-500">{(error as Error).message}</p>
        <button onClick={() => window.location.reload()} className="btn-primary mt-4 text-sm">
          Reload Page
        </button>
      </div>
    );
  }

  const list: SkuSummary[] = data?.skus ?? [];
  const qcCount = list.filter((s) => s.model_type === 'qc').length;
  const poseCount = list.filter((s) => s.model_type === '6dof').length;
  const publishedCount = list.filter((s) => s.status === 'published').length;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">SKU Library</h2>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard label="Total SKUs" value={list.length} />
        <MetricCard label="QC SKUs" value={qcCount} accent="text-blue-400" />
        <MetricCard label="6DoF SKUs" value={poseCount} accent="text-purple-400" />
        <MetricCard label="Published" value={publishedCount} accent="text-emerald-400" />
      </div>

      {/* Train failure feedback — the mutation used to swallow errors silently. */}
      {trainMutation.isError && (
        <div className="mb-4 rounded border border-red-500/40 bg-red-500/10 px-4 py-2 text-sm text-red-300">
          Training could not start: {(trainMutation.error as Error)?.message ?? 'unknown error'}
        </div>
      )}

      {/* SKU table */}
      <div className="card overflow-hidden p-0 overflow-x-auto">
        <div className="px-5 py-3 border-b border-surface-3">
          <h3 className="text-sm font-semibold text-gray-300">SKUs</h3>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-gray-500 text-sm">Loading SKUs...</div>
        ) : list.length === 0 ? (
          <div className="p-8 text-center">
            <Boxes className="mx-auto mb-3 text-gray-600" size={32} />
            <p className="text-sm text-gray-400">No SKUs yet</p>
            <p className="text-xs text-gray-600 mt-1">
              Create a SKU in the Foundry app — pick a model type, upload a CAD model with a pick
              point, then capture and sync its dataset to see it here.
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-3">
                <th className="table-header table-cell text-left">SKU</th>
                <th className="table-header table-cell text-left">Name</th>
                <th className="table-header table-cell text-left">Model Type</th>
                <th className="table-header table-cell text-left">Scope</th>
                <th className="table-header table-cell text-left">Status</th>
                <th className="table-header table-cell text-left">CAD</th>
                <th className="table-header table-cell text-left">Datasets</th>
                <th className="table-header table-cell text-left">Models</th>
                <th className="table-header table-cell text-left">Updated</th>
                <th className="table-header table-cell text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {list.map((s) => (
                <tr key={s.id} className="hover:bg-surface-2/50">
                  <td className="table-cell font-mono text-xs text-kinetica-400">{s.sku_code}</td>
                  <td className="table-cell text-xs text-gray-300">{s.name}</td>
                  <td className="table-cell text-xs text-gray-400">
                    <span className="inline-flex items-center gap-1.5">
                      {s.model_type === '6dof' ? <Crosshair size={12} /> : <Cpu size={12} />}
                      {MODEL_TYPE_LABEL[s.model_type] ?? s.model_type}
                    </span>
                  </td>
                  <td className="table-cell text-xs text-gray-400">
                    {s.scope === 'org' ? 'Shared' : 'Private'}
                  </td>
                  <td className="table-cell">
                    <span className={`text-xs font-medium ${STATUS_COLOR[s.status] ?? 'text-gray-400'}`}>
                      {s.status}
                    </span>
                  </td>
                  <td className="table-cell text-xs">
                    {s.has_cad ? (
                      <span className="inline-flex items-center gap-1 text-emerald-400">
                        <Check size={12} /> STL
                      </span>
                    ) : (
                      <span className="text-gray-600">—</span>
                    )}
                  </td>
                  <td className="table-cell text-xs text-gray-400">
                    {s.dataset_count > 0 ? (
                      `${s.dataset_count}${s.latest_dataset_version ? ` · ${s.latest_dataset_version}` : ''}`
                    ) : (
                      <span className="text-gray-600">—</span>
                    )}
                  </td>
                  <td className="table-cell text-xs text-gray-400">
                    {s.model_count > 0 ? (
                      <span className="inline-flex items-center gap-1">
                        {s.model_count}
                        {s.has_active_model && <Check size={12} className="text-emerald-400" />}
                      </span>
                    ) : (
                      <span className="text-gray-600">—</span>
                    )}
                  </td>
                  <td className="table-cell text-xs text-gray-500">{s.updated_at}</td>
                  <td className="table-cell text-right">
                    {canTrain && s.dataset_count > 0 && s.latest_dataset_version ? (
                      <button
                        onClick={() => {
                          if (s.latest_dataset_version) {
                            trainMutation.mutate({ skuId: s.id, version: s.latest_dataset_version });
                          }
                        }}
                        disabled={trainMutation.isPending}
                        className="text-xs inline-flex items-center gap-1 text-kinetica-400 hover:text-kinetica-300 disabled:opacity-50"
                      >
                        <Rocket size={12} /> Train
                      </button>
                    ) : (
                      <span className="text-gray-600 text-xs">—</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
